import { useMemo, useState, type ReactNode } from "react";

import { LivingBehaviorConstellation } from "@/components/insights/LivingBehaviorConstellation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PatternDetailSheet } from "@/components/coach/sessionPrep/PatternDetailSheet";
import type {
  CoachSessionPrepEmergingPatternItem,
  CoachSessionPrepEmergingPatternsViewModel,
} from "@/lib/behavior/viewModels/buildCoachSessionPrepEmergingPatterns";
import { cn } from "@/lib/utils";

interface EmergingPatternsSectionProps {
  section: CoachSessionPrepEmergingPatternsViewModel;
  leaderId?: string;
  coachVeeraSlot?: ReactNode;
  onOpenEvidenceReference: (reference: {
    label: string;
    link: string | null;
    evidenceId?: string | null;
    viewed?: boolean;
  }) => void;
}

function badgeClassForState(state: CoachSessionPrepEmergingPatternItem["state"]) {
  if (state === "strengthening") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
  if (state === "mixed") return "border-amber-500/30 bg-amber-500/10 text-amber-300";
  return "border-slate-500/30 bg-slate-500/10 text-slate-300";
}

export function EmergingPatternsSection({
  section,
  leaderId,
  coachVeeraSlot,
  onOpenEvidenceReference,
}: EmergingPatternsSectionProps) {
  const [selectedPatternId, setSelectedPatternId] = useState<string | null>(null);
  const selectedPattern = useMemo(
    () => section.patterns.find((pattern) => pattern.id === selectedPatternId) ?? null,
    [section.patterns, selectedPatternId],
  );

  return (
    <>
      <Card className="border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-h3">{section.title}</CardTitle>
          <div className="max-w-3xl space-y-2">
            <p className="text-base text-foreground">{section.headline}</p>
            {section.supportingLine ? (
              <p className="text-sm text-muted-foreground">{section.supportingLine}</p>
            ) : null}
            <p className="text-sm text-muted-foreground">{section.helperText}</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 p-5 pt-0 sm:p-6 sm:pt-0">
          <div
            data-testid="emerging-patterns-top-grid"
            className="space-y-4"
          >
            <div className="rounded-2xl border border-border/60 bg-muted/20 p-4 sm:p-5">
              <LivingBehaviorConstellation leaderId={leaderId} />
            </div>

            {coachVeeraSlot ? (
              <div
                id="section-veera"
                className="rounded-[1.5rem] border border-border/60 bg-[radial-gradient(circle_at_top_left,rgba(169,128,255,0.12),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0))] p-4 sm:p-5 dark:bg-[radial-gradient(circle_at_top_left,rgba(169,128,255,0.14),transparent_38%),linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0))]"
              >
                <div className="mb-3 space-y-1.5">
                  <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">Coach Veera</p>
                  <p className="text-[13px] leading-5 text-muted-foreground">
                    Stay in the same read and ask one follow-up when you need it.
                  </p>
                </div>
                <div>{coachVeeraSlot}</div>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            {section.patterns.map((pattern) => (
              <Button
                key={pattern.id}
                type="button"
                variant="ghost"
                className={cn(
                  "h-auto w-full justify-start rounded-xl border px-4 py-4 text-left",
                  selectedPatternId === pattern.id
                    ? "border-violet-300/70 bg-violet-400/85 text-slate-950 hover:bg-violet-400/85"
                    : "border-border/60 hover:bg-muted/30",
                )}
                onClick={() => setSelectedPatternId(pattern.id)}
              >
                <div className="flex w-full items-start justify-between gap-3">
                  <div className="min-w-0 space-y-2">
                    <p className={cn(
                      "text-sm font-medium leading-6",
                      selectedPatternId === pattern.id ? "text-white" : "text-foreground",
                    )}>{pattern.title}</p>
                    <p className={cn(
                      "text-sm leading-6",
                      selectedPatternId === pattern.id ? "text-slate-900/90" : "text-muted-foreground",
                    )}>{pattern.summary}</p>
                    <p className={cn(
                      "text-xs leading-5",
                      selectedPatternId === pattern.id ? "text-slate-900/80" : "text-muted-foreground",
                    )}>Open to inspect evidence and why it matters.</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2 pt-0.5 text-right">
                    <Badge variant="outline" className={badgeClassForState(pattern.state)}>
                      {pattern.stateLabel}
                    </Badge>
                    <span className={cn(
                      "text-xs",
                      selectedPatternId === pattern.id ? "text-slate-900/80" : "text-muted-foreground",
                    )}>
                      {pattern.evidenceCount} evidence item{pattern.evidenceCount === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <PatternDetailSheet
        open={Boolean(selectedPattern)}
        pattern={selectedPattern}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSelectedPatternId(null);
        }}
        onOpenEvidenceReference={onOpenEvidenceReference}
      />
    </>
  );
}
